/* eslint-disable react-hooks/exhaustive-deps */
 
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-no-leaked-render */
import clsx from 'clsx';
import { CpfInput } from '@/app/components/inputs/CpfInput';
import { CnpjInput } from '@/app/components/inputs/CnpjInput';
import { type FormikProps, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import PasswordChecklist from 'react-password-checklist';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { SystemNotification } from '@/app/components/feedback/SystemNotification';
import * as Yup from 'yup';
import { acceptInvitation, getInvitationValidation } from './core/_requests';
import MetronicCard from '@/app/components/layout/MetronicCard';
import {
  type AcceptInvitationRequestDto,
  initialAcceptInvitationRequestDtoEmpty,
} from '@/app/models/AcceptInvitationRequestDto';

function AcceptanceInvitationPage() {
  const { invitationId } = useParams<{ invitationId: string }>();
  const navigate = useNavigate();
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Busca dados do convite
  const {
    data,
    isPending: isQueryLoading,
    error,
  } = useQuery({
    queryKey: ['invitation-validation', invitationId],
    queryFn: async () => {
      const response = await getInvitationValidation(invitationId!);
      return response.data;
    },
    enabled: !!invitationId,
  });

  const shouldRenderContactForm = useMemo(() => {
    if (data?.type.value === 'ACCOUNTANT') {
      return !data.document_number || !data.email || !data.name || !data.phone || !data.crc;
    }
    if (data?.type.value === 'EMPLOYEE') {
      return !data.name || !data.email || !data.phone;
    }
    return true;
  }, [data]);

  const validationSchema = useMemo(() => {
    const contactShape = {} as { [key: string]: any };

    if (data?.type.value === 'ACCOUNTANT') {
      contactShape.name = Yup.string().required('Obrigatório');
      contactShape.email = Yup.string().email('E-mail inválido').required('Obrigatório');
      contactShape.phone = Yup.string().required('Obrigatório');
      contactShape.document_number = Yup.string().required('Obrigatório');
      contactShape.crc = Yup.string().required('Obrigatório');
    }

    if (data?.type.value === 'EMPLOYEE') {
      contactShape.name = Yup.string().required('Obrigatório');
      contactShape.email = Yup.string().email('E-mail inválido').required('Obrigatório');
      contactShape.phone = Yup.string().required('Obrigatório');
    }

    return Yup.object().shape({
      invitation_id: Yup.string().required('Obrigatório'),
      ...contactShape,
    });
  }, [data]);

  const { mutate: sendInvitationRequest, isPending: isMutationLoading } = useMutation({
    mutationFn: async (values: AcceptInvitationRequestDto) => {
      if (!invitationId) throw new Error('ID do convite não fornecido');

      const response = await acceptInvitation(invitationId, values);

      return response;
    },

    onSuccess: () => {
      SystemNotification.success('Cadastro realizado com sucesso!');
      setIsRedirecting(true);
      setTimeout(() => navigate('/auth/login'), 2000);
    },

    onError: (err: Error) => {
      SystemNotification.error(err.message || 'Erro ao aceitar convite');
    },
  });

  const isPending = useMemo(() => isQueryLoading || isMutationLoading, [isQueryLoading, isMutationLoading]);

  const formik = useFormik<AcceptInvitationRequestDto>({
    initialValues: initialAcceptInvitationRequestDtoEmpty,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      sendInvitationRequest(values);
    },
  });

  const partialUpdate = (updates: Partial<AcceptInvitationRequestDto>) => {
    formik.setValues({
      ...formik.values,
      ...updates,
    });
  };

  useEffect(() => {
    partialUpdate({
      name: data?.name,
      email: data?.email,
      phone: data?.phone,
      document_number: data?.document_number ?? '',
      person_type: data?.person_type ?? { value: 'PHYSICAL', description: 'Pessoa física' },
      crc: data?.crc,
      code: data?.code,
      address: data?.address
        ? {
            street: data.address.street || '',
            neighborhood: data.address.neighborhood || '',
            complement: data.address.complement || '',
            city: data.address.city || '',
            number: data.address.number || '',
            state: data.address.state,
            zip_code: data.address.zip_code || '',
          }
        : undefined,
    });
  }, [data]);

  const onPersonTypeChange = (type: 'PHYSICAL' | 'LEGAL') => {
    formik.setFieldValue(
      'person_type',
      type === 'PHYSICAL' ? { value: 'PHYSICAL', description: 'Física' } : { value: 'LEGAL', description: 'Jurídica' },
    );
  };

  function renderAccountantFields(formik: FormikProps<AcceptInvitationRequestDto>) {
    return (
      <>
        <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
          <div className="fw-semibold">Tipo de documento</div>
          <div aria-label="Alternar documento" className="btn-group" role="group">
            <button
              className={clsx(
                'btn btn-sm',
                formik.values.person_type?.value === 'PHYSICAL' ? 'btn-primary' : 'btn-light-primary',
              )}
              type="button"
              onClick={() => onPersonTypeChange('PHYSICAL')}
            >
              CPF
            </button>
            <button
              className={clsx(
                'btn btn-sm',
                formik.values.person_type?.value === 'LEGAL' ? 'btn-primary' : 'btn-light-primary',
              )}
              type="button"
              onClick={() => onPersonTypeChange('LEGAL')}
            >
              CNPJ
            </button>
          </div>
        </div>
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label className="form-label required">Nome</label>
            <input
              className="form-control"
              name="name"
              placeholder="Seu nome"
              value={formik.values.name ?? ''}
              onChange={formik.handleChange}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label required">
              {formik.values.person_type?.value === 'PHYSICAL' ? 'CPF' : 'CNPJ'}
            </label>
            {formik.values.person_type?.value === 'PHYSICAL' ? (
              <CpfInput
                allowEmptyFormatting
                className="form-control"
                id="document_number"
                mask="_"
                value={formik.values.document_number || ''}
                onValueChange={(values) => {
                  formik.setFieldValue('document_number', values.value);
                }}
              />
            ) : (
              <CnpjInput
                className="form-control"
                id="document_number"
                value={formik.values.document_number || ''}
                onValueChange={(values) => {
                  formik.setFieldValue('document_number', values.value);
                }}
              />
            )}
          </div>
        </div>
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label className="form-label required">E-mail</label>
            <input
              disabled
              className="form-control "
              name="email"
              value={formik.values.email ?? ''}
              onChange={formik.handleChange}
            />
            {!!formik.errors.email && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.email}</div>
              </div>
            )}
          </div>
          <div className="col-md-6">
            <label className="form-label required">Telefone</label>
            <PatternFormat
              className="form-control"
              format="(##) #####-####"
              id="phone"
              placeholder="(xx) xxxxx-xxxx"
              {...formik.getFieldProps('phone')}
            />
            {!!formik.errors.phone && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.phone}</div>
              </div>
            )}
          </div>
        </div>
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label className="form-label required">CRC</label>
            <input
              className="form-control"
              name="crc"
              placeholder="Nº CRC"
              value={formik.values.crc ?? ''}
              onChange={formik.handleChange}
            />
            {!!formik.errors.crc && (
              <div className="fv-plugins-message-container">
                <div className="fv-help-block">{formik.errors.crc}</div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  function renderSystemUserFields(formik: FormikProps<AcceptInvitationRequestDto>) {
    return (
      <>
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label className="form-label required">Nome</label>
            <input
              className="form-control"
              name="name"
              value={formik.values.name ?? ''}
              onChange={formik.handleChange}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label required">Matrícula</label>
            <input
              className="form-control"
              name="code"
              value={formik.values.code ?? ''}
              onChange={formik.handleChange}
            />
          </div>
        </div>
        <div className="row g-4 mb-4">
          <div className="col-md-6">
            <label className="form-label required">E-mail</label>
            <input
              disabled
              className="form-control"
              name="email"
              value={formik.values.email ?? ''}
              onChange={formik.handleChange}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label required">Telefone</label>
            <input
              className="form-control"
              name="phone"
              value={formik.values.phone ?? ''}
              onChange={formik.handleChange}
            />
          </div>
        </div>
      </>
    );
  }

  function renderContactFields(type: string, formik: FormikProps<any>) {
    switch (type) {
      case 'ACCOUNTANT':
        return renderAccountantFields(formik);
      case 'EMPLOYEE':
        return renderSystemUserFields(formik);
      default:
        return null;
    }
  }

  useEffect(() => {
    formik.setFieldValue('invitation_id', invitationId);
  }, [invitationId]);

  if (isPending || isRedirecting) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
        <div className="text-center py-20">
          <div className="spinner-border text-primary mb-4" />
          <div className="fw-bold fs-3">Carregando aguarde...</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center min-vh-100 bg-light">
        <div className="text-center py-20">
          <img alt="Erro" className="mb-6" src="/media/illustrations/misc/upgrade.svg" style={{ maxWidth: 180 }} />
          <div className="fw-bold fs-3 mb-2">Convite inválido ou expirado</div>
          <div className="text-gray-600">Verifique o link ou solicite um novo convite ao administrador.</div>
        </div>
      </div>
    );
  }

  // Layout
  return (
    <div className="d-flex flex-column align-items-center justify-content-center bg-light">
      <div className="container py-10">
        <div className="mx-auto" style={{ maxWidth: 700 }}>
          <form autoComplete="off" onSubmit={formik.handleSubmit}>
            <MetronicCard className="card-flush mb-8">
              <div className="card-header border-0 pb-0">
                <div className="card-title">
                  <h4 className="mb-0">Complete seu cadastro</h4>
                </div>
                <div className="card-toolbar">
                  <span className="text-gray-500 fs-7">
                    Preencha os dados abaixo para completar seu registro no sistema.
                  </span>
                </div>
              </div>
              <div className="card-body">
                {shouldRenderContactForm ? (
                  renderContactFields(data.type.value, formik)
                ) : (
                  <div className="mb-6 text-center">
                    <h3 className="fw-bold mb-2">Olá {data.name && `, ${data.name}`}!</h3>
                    <div className="bg-light rounded p-4 mb-6 d-inline-block text-start w-100">
                      <div className="row mb-2">
                        <div className="col-6">
                          <div className="text-gray-600 fs-8">Email Cadastrado</div>
                          <div className="fw-semibold">{data.email}</div>
                        </div>
                        <div className="col-6">
                          <div className="text-gray-600 fs-8">Status</div>
                          <div className="fw-semibold">{data.status.description}</div>
                        </div>
                      </div>
                      {data.address != undefined && (
                        <div className="row">
                          <div className="col-12">
                            <div className="text-gray-600 fs-8">Endereço Principal</div>
                            <div className="fw-semibold">{`${data.address.street ?? ''}, ${data.address.number ?? ''} - ${data.address.neighborhood ?? ''}, ${data.address.city ?? ''}`}</div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="mb-2">
                      Você foi convidado para se juntar à equipe. Confirme seus dados e crie sua senha para ativar a
                      conta.
                    </div>
                  </div>
                )}
              </div>
            </MetronicCard>

            {/* Dados de acesso */}
            <MetronicCard className="card-flush">
              <div className="card-header border-0 pb-0">
                <div className="card-title">
                  <h4 className="mb-0">Dados de acesso</h4>
                </div>
                <div className="card-toolbar">
                  <span className="text-gray-500 fs-7">Preencha os dados de acesso no sistema.</span>
                </div>
              </div>
              <div className="card-body">
                <div className="mb-4">
                  <label className="form-label">Nova Senha</label>
                  <input
                    className={clsx('form-control', formik.touched.password && formik.errors.password && 'is-invalid')}
                    name="password"
                    placeholder="Sua senha"
                    type="password"
                    value={formik.values.password}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  {formik.touched.password == true && formik.errors.password != undefined && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{formik.errors.password}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <label className="form-label">Confirmar Senha</label>
                  <input
                    className={clsx(
                      'form-control',
                      formik.touched.confirm_password && formik.errors.confirm_password && 'is-invalid',
                    )}
                    name="confirm_password"
                    placeholder="Confirme sua senha"
                    type="password"
                    value={formik.values.confirm_password}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                  />
                  {!!formik.touched.confirm_password && !!formik.errors.confirm_password && (
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <span role="alert">{formik.errors.confirm_password}</span>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mb-4">
                  <div className="bg-light rounded p-4">
                    <div className="mb-1 fw-semibold">Requisitos da senha</div>
                    <PasswordChecklist
                      iconSize={12}
                      invalidColor="#D12446"
                      messages={{
                        minLength: 'Mínimo 8 caracteres',
                        specialChar: 'Pelo menos um caractere especial',
                        number: 'Pelo menos um número',
                        capital: 'Pelo menos uma letra maiúscula',
                        match: 'Senhas coincidem',
                      }}
                      minLength={8}
                      rules={['minLength', 'capital', 'number', 'match']}
                      validColor="#4BCA81"
                      value={formik.values.password}
                      valueAgain={formik.values.confirm_password}
                      onChange={(isValid) => setIsPasswordValid(isValid)}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end">
                  <button className="btn btn-primary px-8" disabled={isPending || !isPasswordValid} type="submit">
                    {isMutationLoading ? 'Enviando...' : 'Definir senha e acessar'}
                  </button>
                </div>
              </div>
            </MetronicCard>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AcceptanceInvitationPage;
